import { useState } from "react";
import { CheckCircle2, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import BulkDropzone from "@/components/BulkDropzone";
import api, { formatApiErrorDetail } from "@/lib/api";
import { findBestMatch } from "@/lib/fuzzyMatch";
import { LANDING_ADMIN } from "@/constants/testIds";

const VIDEO_EXTENSIONS = ["mp4", "mov", "webm"];

/** Carga masiva de la galería de resultados: arrastrá/seleccioná varias fotos
 * (o videos) y se intentan asociar a un tratamiento por el nombre del
 * archivo. Cada fila se puede corregir u omitir antes de confirmar. */
export default function BulkGalleryUploadModal({ open, onOpenChange, services, onDone }) {
  const [step, setStep] = useState("drop"); // drop | review | uploading | result
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [result, setResult] = useState(null);

  const reset = () => {
    rows.forEach((r) => URL.revokeObjectURL(r.previewUrl));
    setStep("drop"); setRows([]); setProgress({ done: 0, total: 0 }); setResult(null);
  };

  const handleClose = (isOpen) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  const handleFiles = (files) => {
    const built = files.map((file, idx) => {
      const ext = file.name.split(".").pop()?.toLowerCase();
      const isVideo = VIDEO_EXTENSIONS.includes(ext);
      const { match } = findBestMatch(file.name, services, (s) => s.name);
      return {
        key: `${idx}-${file.name}`,
        file,
        isVideo,
        previewUrl: URL.createObjectURL(file),
        filename: file.name,
        serviceId: match ? match.id : "none",
      };
    });
    setRows(built);
    setStep("review");
  };

  const updateRow = (key, serviceId) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, serviceId } : r)));
  const removeRow = (key) => setRows((rs) => rs.filter((r) => r.key !== key));

  const handleConfirm = async () => {
    setStep("uploading");
    let success = 0;
    const errors = [];
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      setProgress({ done: i, total: rows.length });
      try {
        const formData = new FormData();
        formData.append("file", row.file);
        const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
        await api.post("/landing/gallery", {
          image_url: data.url,
          media_type: row.isVideo ? "video" : "image",
          service_id: row.serviceId === "none" ? null : row.serviceId,
        });
        success++;
      } catch (err) {
        errors.push({ filename: row.filename, error: formatApiErrorDetail(err.response?.data?.detail) });
      }
    }
    setProgress({ done: rows.length, total: rows.length });
    setResult({ success, errors });
    setStep("result");
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid={LANDING_ADMIN.galleryBulkModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Subir galería en lote</DialogTitle>
        </DialogHeader>

        {step === "drop" && (
          <div className="py-2">
            <BulkDropzone
              onFiles={handleFiles}
              accept="image/png,image/jpeg,image/jpg,image/webp,image/avif,video/mp4"
              label="Arrastrá o hacé click para elegir varias fotos o videos"
              hint="Se intentan asociar a un tratamiento por el nombre del archivo. Podés corregirlo antes de confirmar."
              testId={LANDING_ADMIN.galleryBulkDropzone}
            />
          </div>
        )}

        {step === "review" && (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">{rows.length} archivo(s)</p>
            <div className="border border-border rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
              <Table data-testid={LANDING_ADMIN.galleryBulkTable}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16"></TableHead>
                    <TableHead>Archivo</TableHead>
                    <TableHead>Tratamiento asociado</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, idx) => (
                    <TableRow key={r.key} data-testid={`${LANDING_ADMIN.galleryBulkRowPrefix}${idx}`}>
                      <TableCell>
                        {r.isVideo ? (
                          <video src={r.previewUrl} className="w-12 h-12 object-cover rounded border border-border" muted />
                        ) : (
                          <img src={r.previewUrl} alt={r.filename} className="w-12 h-12 object-cover rounded border border-border" />
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm truncate max-w-[220px]">{r.filename}</p>
                        {r.serviceId === "none" && (
                          <span className="text-[11px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded" data-testid={`${LANDING_ADMIN.galleryBulkNoMatchBadgePrefix}${idx}`}>Sin coincidencia</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Select value={r.serviceId} onValueChange={(v) => updateRow(r.key, v)}>
                          <SelectTrigger className="w-56 h-8 text-xs" data-testid={`${LANDING_ADMIN.galleryBulkServiceSelectPrefix}${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin tratamiento asociado</SelectItem>
                            {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => removeRow(r.key)} data-testid={`${LANDING_ADMIN.galleryBulkRemoveButtonPrefix}${idx}`}>
                          <X className="w-3.5 h-3.5 text-muted-foreground" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {step === "uploading" && (
          <div className="py-10 space-y-3" data-testid={LANDING_ADMIN.galleryBulkProgress}>
            <p className="text-sm text-center text-muted-foreground">Subiendo {progress.done} de {progress.total}...</p>
            <Progress value={progress.total ? (progress.done / progress.total) * 100 : 0} />
          </div>
        )}

        {step === "result" && result && (
          <div className="py-6 text-center space-y-3" data-testid={LANDING_ADMIN.galleryBulkResultSummary}>
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <p className="text-lg font-serif text-foreground">Carga completa</p>
            <p className="text-sm text-muted-foreground">{result.success} archivo(s) cargados &middot; {result.errors.length} error(es)</p>
            {result.errors.length > 0 && (
              <ul className="text-xs text-destructive text-left max-w-sm mx-auto space-y-1">
                {result.errors.map((e, i) => <li key={i}>{e.filename}: {e.error}</li>)}
              </ul>
            )}
          </div>
        )}

        <DialogFooter>
          {step === "drop" && (
            <Button variant="outline" onClick={() => handleClose(false)} data-testid={LANDING_ADMIN.galleryBulkCancelButton}>Cancelar</Button>
          )}
          {step === "review" && (
            <>
              <Button variant="outline" onClick={reset} data-testid={LANDING_ADMIN.galleryBulkBackButton}>Volver</Button>
              <Button onClick={handleConfirm} disabled={rows.length === 0} data-testid={LANDING_ADMIN.galleryBulkConfirmButton}>Confirmar carga ({rows.length})</Button>
            </>
          )}
          {step === "result" && <Button onClick={() => handleClose(false)}>Cerrar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
